/**
 * BaseModal - 通用弹窗基础组件
 *
 * 基于 Radix UI Dialog 实现的统一弹窗组件，提供遮罩层、关闭按钮等功能
 *
 * @module BaseModal
 * @date 2026-01-29
 */

'use client'

import React from 'react'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { Heading } from '../../components/index.js'
import { cn } from '../../helpers/index.js'

export interface BaseModalProps {
  /**
   * 是否显示弹窗
   */
  isOpen: boolean

  /**
   * 关闭弹窗回调
   */
  onClose: () => void

  /**
   * 弹窗内容
   */
  children: React.ReactNode

  /**
   * 弹窗最大宽度类名
   * @default 'max-w-[358px] laptop:max-w-[480px]'
   */
  maxWidth?: string

  /**
   * 弹窗背景渐变
   * @default 'bg-white'
   */
  background?: string

  /**
   * 是否显示关闭按钮
   * @default true
   */
  showCloseButton?: boolean

  /**
   * 自定义类名
   */
  className?: string

  /**
   * title 弹窗标题
   */
  title?: string

  description?: string

  /**
   * ARIA 标签
   */
  ariaLabelledBy?: string

  /**
   * 主题模式
   * @default 'light'
   */
  theme?: 'light' | 'dark'
}

/**
 * BaseModal 通用弹窗组件（基于 Radix UI Dialog）
 */
export const BaseModal: React.FC<BaseModalProps> = ({
  isOpen,
  onClose,
  children,
  maxWidth = 'max-w-[358px] laptop:max-w-[480px]',
  background = 'bg-white',
  showCloseButton = true,
  className,
  title,
  description,
  ariaLabelledBy,
  theme = 'light',
}) => {
  const titleId = ariaLabelledBy || undefined
  const descriptionId = `${ariaLabelledBy || 'base-modal'}-description`
  const resolvedTitle = title || 'Dialog'
  const resolvedDescription = description || title || 'Dialog content'

  return (
    <DialogPrimitive.Root open={isOpen} onOpenChange={open => !open && onClose()}>
      <DialogPrimitive.Portal>
        {/* 遮罩层 */}
        <DialogPrimitive.Overlay
          className={cn(
            'fixed inset-0 z-[60]',
            'bg-black/40',
            // Radix UI 动画
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
            theme === 'dark' ? 'aiui-dark' : ''
          )}
        />

        {/* 弹窗内容 */}
        <DialogPrimitive.Content
          className={cn(
            'fixed left-1/2 top-1/2 z-[70]',
            '-translate-x-1/2 -translate-y-1/2',
            'l-tablet:max-w-[90%] w-full shadow-2xl',
            'rounded-box',
            maxWidth,
            background,
            // Radix UI 动画
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
            'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
            'duration-300',
            className
          )}
          aria-labelledby={titleId}
          aria-describedby={descriptionId}
          aria-modal="true"
        >
          <DialogPrimitive.Title id={titleId} className="sr-only">
            {resolvedTitle}
          </DialogPrimitive.Title>

          <DialogPrimitive.Description id={descriptionId} className="sr-only">
            {resolvedDescription}
          </DialogPrimitive.Description>

          {/* 标题和关闭按钮栏 */}
          <div className={cn('modal-close', 'flex items-center justify-between', 'laptop:p-4 px-2 pb-1 pt-2')}>
            {title && (
              <Heading size={2} className="tracking-[-0.32px]">
                {title}
              </Heading>
            )}
            {showCloseButton && (
              <DialogPrimitive.Close asChild>
                <button
                  className={cn(
                    'relative z-10 shrink-0 cursor-pointer',
                    'flex items-center justify-center',
                    'laptop:size-[24px] size-[20px]',
                    'desktop:size-[28px]',
                    // 移除 title 时自动居右
                    !title && 'ml-auto'
                  )}
                  aria-label="关闭弹窗"
                >
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="size-full"
                  >
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </DialogPrimitive.Close>
            )}
          </div>

          {/* 弹窗内容 */}
          {children}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}

export default BaseModal
